import React, { useEffect, useState } from 'react';
import { api } from '../../api/client';

import { ta } from '../../i18n/adminT';
export default function KioskSettings() {
  const [adminLinkVisible, setAdminLinkVisible] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const [signatureEnabled, setSignatureEnabled] = useState(false);
  const [signatureRequired, setSignatureRequired] = useState(true);
  const [signatureSaving, setSignatureSaving] = useState(false);
  const [signatureSaved, setSignatureSaved] = useState(false);
  const [signatureError, setSignatureError] = useState('');

  const [documentFieldEnabled, setDocumentFieldEnabled] = useState(true);
  const [nfcFirst, setNfcFirst] = useState(false);
  const [nfcFirstSaving, setNfcFirstSaving] = useState(false);
  const [nfcFirstSaved, setNfcFirstSaved] = useState(false);
  const [nfcFirstError, setNfcFirstError] = useState('');
  const [documentSaving, setDocumentSaving] = useState(false);
  const [documentSaved, setDocumentSaved] = useState(false);
  const [documentError, setDocumentError] = useState('');

  const [badgeSignatureEnabled, setBadgeSignatureEnabled] = useState(false);
  const [badgeSignatureSaving, setBadgeSignatureSaving] = useState(false);
  const [badgeSignatureSaved, setBadgeSignatureSaved] = useState(false);
  const [badgeSignatureError, setBadgeSignatureError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getKioskConfig();
      setAdminLinkVisible(res?.data?.adminLinkVisible !== false);
    } catch {
      // in caso di errore restano i valori di default
    }
    try {
      const res = await api.getSignatureConfig();
      setSignatureEnabled(Boolean(res?.data?.enabled));
      setSignatureRequired(res?.data?.required !== false);
    } catch {
      // in caso di errore restano i valori di default
    }
    try {
      const res = await api.getBadgeRegistrationConfigAdmin();
      setNfcFirst(res?.data?.nfcFirst === true);
    } catch {
      // in caso di errore resta la modalità storica (campo badge nel modulo)
    }
    try {
      const res = await api.getDocumentFieldConfig();
      setDocumentFieldEnabled(res?.data?.enabled !== false);
    } catch {
      // in caso di errore resta attivo (comportamento storico)
    }
    try {
      const res = await api.getBadgeSignatureConfig();
      setBadgeSignatureEnabled(Boolean(res?.data?.enabled));
    } catch {
      // in caso di errore resta disattivato
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    setError('');
    setSaved(false);
    setSaving(true);
    try {
      await api.saveKioskConfig({ adminLinkVisible });
      setSaved(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSignature = async () => {
    setSignatureError('');
    setSignatureSaved(false);
    setSignatureSaving(true);
    try {
      await api.saveSignatureConfig({ enabled: signatureEnabled, required: signatureRequired });
      setSignatureSaved(true);
    } catch (err) {
      setSignatureError(err.message);
    } finally {
      setSignatureSaving(false);
    }
  };

  const handleSaveNfcFirst = async () => {
    setNfcFirstError('');
    setNfcFirstSaved(false);
    setNfcFirstSaving(true);
    try {
      await api.saveBadgeRegistrationConfig({ nfcFirst });
      setNfcFirstSaved(true);
    } catch (err) {
      setNfcFirstError(err.message);
    } finally {
      setNfcFirstSaving(false);
    }
  };

  const handleSaveDocument = async () => {
    setDocumentError('');
    setDocumentSaved(false);
    setDocumentSaving(true);
    try {
      await api.saveDocumentFieldConfig({ enabled: documentFieldEnabled });
      setDocumentSaved(true);
    } catch (err) {
      setDocumentError(err.message);
    } finally {
      setDocumentSaving(false);
    }
  };

  const handleSaveBadgeSignature = async () => {
    setBadgeSignatureError('');
    setBadgeSignatureSaved(false);
    setBadgeSignatureSaving(true);
    try {
      await api.saveBadgeSignatureConfig({ enabled: badgeSignatureEnabled });
      setBadgeSignatureSaved(true);
    } catch (err) {
      setBadgeSignatureError(err.message);
    } finally {
      setBadgeSignatureSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div>
      <h1 className="admin-page-title">{ta('Pannello utente')}</h1>
      <p className="admin-page-subtitle">{ta('Impostazioni della schermata principale mostrata sul dispositivo.')}</p>

      <div className="admin-card" style={{ maxWidth: 480 }}>
        {error && <div className="form-error">{error}</div>}
        {saved && (
          <div style={{ background: 'rgba(62,142,126,0.12)', color: 'var(--teal-green-dark)', borderRadius: 'var(--radius-control)', padding: '10px 14px', fontSize: '0.85rem', marginBottom: 16 }}>
            {ta('Impostazioni salvate.')}
          </div>
        )}

        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={adminLinkVisible}
            onChange={(e) => { setAdminLinkVisible(e.target.checked); setSaved(false); }}
          />
          <span>{ta('Mostra il link "Accesso amministratore" nella schermata principale')}</span>
        </label>

        <p style={{ fontSize: '0.78rem', color: 'var(--muted-text)', marginTop: -4, marginBottom: 16 }}>
          {ta('Disattivandolo, il link scompare dalla schermata che vedono i visitatori — utile per non attirare l\'attenzione su quella funzione su un chiosco pubblico. È solo estetico: la pagina di accesso resta comunque raggiungibile digitando')} <span className="mono">{ta('/admin')}</span> {ta('nell\'indirizzo, non è una restrizione di sicurezza vera e propria.')}
        </p>

        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving}
          style={{ width: 'auto', padding: '10px 20px' }}
        >
          {saving ? ta('Salvataggio…') : ta('Salva')}
        </button>
      </div>

      <div className="admin-card" style={{ maxWidth: 480 }}>
        <h3 style={{ marginBottom: 10 }}>{ta('Badge in fase di registrazione')}</h3>
        {nfcFirstError && <div className="form-error">{nfcFirstError}</div>}
        {nfcFirstSaved && (
          <div style={{ background: 'rgba(62,142,126,0.12)', color: 'var(--teal-green-dark)', borderRadius: 'var(--radius-control)', padding: '10px 14px', fontSize: '0.85rem', marginBottom: 16 }}>
            {ta('Impostazioni salvate.')}
          </div>
        )}

        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={nfcFirst}
            onChange={(e) => { setNfcFirst(e.target.checked); setNfcFirstSaved(false); }}
          />
          <span>{ta('Chiedi il badge alla fine, passandolo sul lettore NFC')}</span>
        </label>

        <p style={{ fontSize: '0.78rem', color: 'var(--muted-text)', marginTop: -4, marginBottom: 16 }}>
          {ta('Se attivo, il modulo "Registrati" non mostra più il campo numero badge: il visitatore compila i dati, firma e accetta le informative, e solo alla fine gli viene chiesto di passare il badge sul lettore. Sotto c\'è il tasto "Inserisci a mano" per digitare il numero da tastiera. Vale solo sui dispositivi segnati con "Lettore badge" in Dispositivi autorizzati: sugli altri il modulo resta come è sempre stato, con il campo numero badge.')}
        </p>

        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSaveNfcFirst}
          disabled={nfcFirstSaving}
          style={{ width: 'auto', padding: '10px 20px' }}
        >
          {nfcFirstSaving ? ta('Salvataggio…') : ta('Salva')}
        </button>
      </div>

      <div className="admin-card" style={{ maxWidth: 480 }}>
        <h3 style={{ marginBottom: 10 }}>{ta('Campo documento d\'identità')}</h3>
        {documentError && <div className="form-error">{documentError}</div>}
        {documentSaved && (
          <div style={{ background: 'rgba(62,142,126,0.12)', color: 'var(--teal-green-dark)', borderRadius: 'var(--radius-control)', padding: '10px 14px', fontSize: '0.85rem', marginBottom: 16 }}>
            {ta('Impostazioni salvate.')}
          </div>
        )}

        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={documentFieldEnabled}
            onChange={(e) => { setDocumentFieldEnabled(e.target.checked); setDocumentSaved(false); }}
          />
          <span>{ta('Mostra il campo documento d\'identità nel modulo di registrazione')}</span>
        </label>

        <p style={{ fontSize: '0.78rem', color: 'var(--muted-text)', marginTop: -4, marginBottom: 16 }}>
          {ta('Se disattivato, il modulo "Registrati" non mostra tipo e numero documento. Quando è visibile resta comunque facoltativo per il visitatore, come è sempre stato.')}
        </p>

        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSaveDocument}
          disabled={documentSaving}
          style={{ width: 'auto', padding: '10px 20px' }}
        >
          {documentSaving ? ta('Salvataggio…') : ta('Salva')}
        </button>
      </div>

      <div className="admin-card" style={{ maxWidth: 480 }}>
        <h3 style={{ marginBottom: 10 }}>{ta('Firma in fase di registrazione')}</h3>
        {signatureError && <div className="form-error">{signatureError}</div>}
        {signatureSaved && (
          <div style={{ background: 'rgba(62,142,126,0.12)', color: 'var(--teal-green-dark)', borderRadius: 'var(--radius-control)', padding: '10px 14px', fontSize: '0.85rem', marginBottom: 16 }}>
            {ta('Impostazioni salvate.')}
          </div>
        )}

        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={signatureEnabled}
            onChange={(e) => { setSignatureEnabled(e.target.checked); setSignatureSaved(false); }}
          />
          <span>{ta('Mostra il campo firma nel modulo di registrazione')}</span>
        </label>

        <label className="checkbox-row" style={{ opacity: signatureEnabled ? 1 : 0.45 }}>
          <input
            type="checkbox"
            checked={signatureRequired}
            disabled={!signatureEnabled}
            onChange={(e) => { setSignatureRequired(e.target.checked); setSignatureSaved(false); }}
          />
          <span>{ta('Rendi la firma obbligatoria per completare la registrazione')}</span>
        </label>

        <p style={{ fontSize: '0.78rem', color: 'var(--muted-text)', marginTop: -4, marginBottom: 16 }}>
          {ta('Se il campo è visibile ma non obbligatorio, il visitatore può comunque firmare, ma può anche completare la registrazione senza farlo. Se il campo è nascosto, non compare proprio, indipendentemente dalla seconda opzione.')}
        </p>

        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSaveSignature}
          disabled={signatureSaving}
          style={{ width: 'auto', padding: '10px 20px' }}
        >
          {signatureSaving ? ta('Salvataggio…') : ta('Salva')}
        </button>
      </div>

      <div className="admin-card" style={{ maxWidth: 480 }}>
        <h3 style={{ marginBottom: 10 }}>{ta('Firma al check-in con badge')}</h3>
        {badgeSignatureError && <div className="form-error">{badgeSignatureError}</div>}
        {badgeSignatureSaved && (
          <div style={{ background: 'rgba(62,142,126,0.12)', color: 'var(--teal-green-dark)', borderRadius: 'var(--radius-control)', padding: '10px 14px', fontSize: '0.85rem', marginBottom: 16 }}>
            {ta('Impostazioni salvate.')}
          </div>
        )}

        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={badgeSignatureEnabled}
            onChange={(e) => { setBadgeSignatureEnabled(e.target.checked); setBadgeSignatureSaved(false); }}
          />
          <span>{ta('Richiedi la firma quando un utente ricorrente timbra con badge o codice')}</span>
        </label>

        <p style={{ fontSize: '0.78rem', color: 'var(--muted-text)', marginTop: -4, marginBottom: 16 }}>
          {ta('Riguarda solo il tasto "Badge" del pannello utente (utenti esterni pre-registrati), non il modulo "Registrati". Se attiva, dopo aver riconosciuto la persona dal badge o dal codice, viene chiesta comunque la firma prima di completare la registrazione: a differenza della firma nel modulo "Registrati", qui non c\'è una versione facoltativa — se questa opzione è attiva, la firma è sempre richiesta, perché il suo scopo è proprio essere una prova dell\'ingresso effettivo.')}
        </p>

        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSaveBadgeSignature}
          disabled={badgeSignatureSaving}
          style={{ width: 'auto', padding: '10px 20px' }}
        >
          {badgeSignatureSaving ? ta('Salvataggio…') : ta('Salva')}
        </button>
      </div>
    </div>
  );
}
